import { PlainMessage } from '@bufbuild/protobuf';
import {
  MailingList,
  MailingListRole,
} from '@q6cyber/proto2es/generated/client';
import { PhoenixTrpcClient } from 'apps/phoenix/src/app/utils';
import { Observable, catchError, forkJoin, from, map, of } from 'rxjs';

import { Component, Input, inject } from '@angular/core';
import { FormControl } from '@angular/forms';

import {
  AutocompleteOption,
  ChipsServerSideAutocomplete,
} from '../../interfaces';

type MailingListValue = PlainMessage<MailingList>;

@Component({
  selector: 'phoenix-mailing-list-autocomplete',
  templateUrl: './mailing-list-autocomplete.component.html',
  standalone: false,
})
export class MailingListAutocompleteComponent {
  readonly intellihawqFileshareIcon = 'check_circle';
  readonly intellihawqFileshareLabel = 'IntellihawQ Fileshare';
  @Input() control = new FormControl<MailingListValue[]>([]);
  @Input() title = 'Mailing Lists';
  @Input() ariaLabel = 'clientSubscriptionMailingLists';
  @Input() clientId = '';

  mailingListsSearchTerm = '';
  phoenixTrpc = inject(PhoenixTrpcClient);

  autocompleteConfig: ChipsServerSideAutocomplete = {
    attrFilter: 'name',
    customFilter: mailingList => this.displayValue(mailingList),
    serverCallback: (search: string): Observable<MailingListValue[]> =>
      this.getMailingLists(search),
    pasteCallback: (value: string): Observable<MailingListValue[]> =>
      this.getMailingListsFromPastedNames(value),
    optionHtml: option => this.getOptionHtml(option),
    fetchOnFocus: true,
    noOptionsMessage: 'No mailing lists found',
  };

  getMailingLists(search: string): Observable<MailingListValue[]> {
    this.mailingListsSearchTerm = search;
    const normalizedClientId = this.clientId.trim();
    const normalizedSearch = search.trim();

    return from(
      this.phoenixTrpc.client.getMailingLists.query({
        ...(normalizedClientId ? { clientId: normalizedClientId } : {}),
        search: normalizedSearch,
      })
    ).pipe(
      map(mailingLists => this.filterSelectedMailingLists(mailingLists)),
      catchError(() => of([]))
    );
  }

  filterSelectedMailingLists(
    mailingLists: MailingListValue[]
  ): MailingListValue[] {
    const selectedMailingListKeys = new Set(
      (this.control.value ?? []).map(mailingList =>
        this.getMailingListKey(mailingList)
      )
    );

    return mailingLists.filter(
      mailingList =>
        !selectedMailingListKeys.has(this.getMailingListKey(mailingList))
    );
  }

  getMailingListKey(mailingList: MailingListValue): string {
    return (
      mailingList.id ||
      `${mailingList.name ?? ''}:${(mailingList.emails ?? []).join()}`
    ).toLowerCase();
  }

  getMailingListsFromPastedNames(
    pastedValue: string
  ): Observable<MailingListValue[]> {
    const mailingListNames = this.parseMailingListNames(pastedValue);

    if (!mailingListNames.length) {
      return of([]);
    }

    return forkJoin(
      mailingListNames.map(name =>
        this.getMailingLists(name).pipe(
          map(mailingLists => this.findExactMailingList(mailingLists, name))
        )
      )
    ).pipe(
      map(mailingLists =>
        this.removeDuplicateMailingLists(
          mailingLists.filter(Boolean) as MailingListValue[]
        )
      ),
      catchError(() => of([]))
    );
  }

  parseMailingListNames(value: string): string[] {
    return value
      .split(/[,\n]+/)
      .map(name => name.trim())
      .filter(Boolean);
  }

  findExactMailingList(
    mailingLists: MailingListValue[],
    mailingListName: string
  ): MailingListValue | undefined {
    const normalizedMailingListName =
      this.normalizeMailingListName(mailingListName);

    return mailingLists.find(
      mailingList =>
        this.normalizeMailingListName(mailingList.name ?? '') ===
        normalizedMailingListName
    );
  }

  removeDuplicateMailingLists(
    mailingLists: MailingListValue[]
  ): MailingListValue[] {
    const selectedMailingListKeys = new Set<string>();

    return mailingLists.filter(mailingList => {
      const mailingListKey = this.getMailingListKey(mailingList);
      if (selectedMailingListKeys.has(mailingListKey)) {
        return false;
      }
      selectedMailingListKeys.add(mailingListKey);
      return true;
    });
  }

  normalizeMailingListName(value: string): string {
    return value.trim().toLowerCase();
  }

  displayValue = (mailingList: AutocompleteOption | string): string => {
    if (typeof mailingList !== 'object' || mailingList === null) {
      return `${mailingList}`;
    }

    const mailingListValue = mailingList as MailingListValue;
    const clientId = mailingListValue.id?.split(':')[0];
    return clientId
      ? `${clientId} - ${mailingListValue.name ?? ''}`
      : (mailingListValue.name ?? '');
  };

  getOptionHtml(mailingList: MailingListValue): string {
    const label = this.highlightSearchTerm(this.displayValue(mailingList));

    if (!this.hasIntellihawqFileshareRole(mailingList)) {
      return label;
    }

    return [
      '<div class="flex items-center justify-between gap-2 w-full">',
      `<span>${label}</span>`,
      `<span class="flex ml-4 items-center gap-1 flex-shrink-0 green-theme-text-1" title="${this.intellihawqFileshareLabel}" aria-label="${this.intellihawqFileshareLabel}">`,
      '<span class="material-icons-outlined text-[18px] leading-none">check_circle</span>',
      `<span class="text-xs leading-none">${this.intellihawqFileshareLabel}</span>`,
      '</span>',
      '</div>',
    ].join('');
  }

  mailingListIconText = (
    mailingList: MailingListValue | string
  ): string | null =>
    typeof mailingList === 'object' &&
    mailingList !== null &&
    this.hasIntellihawqFileshareRole(mailingList)
      ? this.intellihawqFileshareIcon
      : null;

  hasIntellihawqFileshareRole(mailingList: MailingListValue): boolean {
    const intellihawqFileshareRole =
      MailingListRole[MailingListRole.INTELLIHAWQ_FILESHARE];

    return (mailingList.role ?? []).some(role => {
      const mailingListRole = role as MailingListRole | string;
      return (
        mailingListRole === MailingListRole.INTELLIHAWQ_FILESHARE ||
        mailingListRole === intellihawqFileshareRole
      );
    });
  }

  highlightSearchTerm(value: string): string {
    const rawSearchTerm = this.mailingListsSearchTerm.trim();
    const searchTerm = this.escapeRegExp(rawSearchTerm);

    if (!searchTerm) {
      return this.escapeHtml(value);
    }

    return value
      .split(new RegExp(`(${searchTerm})`, 'gi'))
      .map(segment =>
        segment.toLowerCase() === rawSearchTerm.toLowerCase()
          ? `<strong>${this.escapeHtml(segment)}</strong>`
          : this.escapeHtml(segment)
      )
      .join('');
  }

  escapeHtml(value: string): string {
    return value.replace(
      /[&<>"']/g,
      character =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        })[character] ?? character
    );
  }

  escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  }
}
